/**
 * MetricsDiagnosisPage — 数据中台 · L3 诊断下钻（ADM-MTR-S006，1:1 移植原型 metrics-diagnosis.jsx）
 *
 * 维度组合查询器（渠道多选，服务端过滤：CSV 进 query 串）+ 明细表（Installs live · 收入列
 * Rev_D7/LTV_D30 按服务端 PENDING 闸控灰显）。hour_bucket/book_id 维度无数据源（服务端非空
 * 过滤即 422）→ 按钮禁用 + tooltip 说明，不发请求（禁「看似能选实则 422」的控件）。
 * 数据走 useMetricsDiagnosis（GET /api/v1/metrics/diagnosis，admin 自有契约 carve-out）。
 * 禁假数据：未就绪指标一律 PENDING 灰显（禁示例值）；空态（无归因行）≠ PENDING；
 * 原型 SampleTag / 演示 mode 切换器不移植。
 *
 * 接真偏离（记账）：
 * - 原型「导出明细」按钮接 dash-export 弹窗（= 数据总览周报导出 POST /dashboard/export，导出
 *   dashboard KPI 而非本页明细行 · 原型接线问题）→ 改为客户端 CSV 导出当前 data.rows（就地
 *   生成，无需 diagnosis 明细导出端点）；未就绪收入列导出标 PENDING（保留灰显语义、禁导出假数字）。
 * - 原型组合计数「x / y 组合」为客户端过滤（y=全集）；impl 过滤在服务端，全集行数未知 →
 *   只显示当前结果行数「x 组合」。
 * - 原型渠道选项写死展示名（Meta Ads/AppLovin/自然量）；impl 从当前结果 distinct channel ∪
 *   已选值派生（真实 media_source 值）。自然量（media_source=''）不入选项：route CSV 解析去
 *   空串，'' 无法作为过滤值下发（同禁「看似能选实则无效」）。
 */

import type { MetricsRange } from '@novel-admin/shared';
import { useEffect, useMemo, useState } from 'react';
import { useAppScope } from '~/app/app-scope-store';
import type {
  DerivedMetricCell,
  DiagnosisRowView,
} from '~/data/adapters/metrics-diagnosis-adapter';
import { useApps, useMetricsDiagnosis } from '~/data/queries';
import { Icons } from '~/lib/icons';
import { FilterButton } from '~/ui/FilterButton';
import { TableSkeleton } from '~/ui/Skeleton';
import { Table } from '~/ui/Table';
import { toast } from '~/ui/Toast';
import { IncomeCoverageNotice } from './IncomeCoverageNotice';
import {
  Cal,
  EmptyBlock,
  LiveTag,
  MetricsToolbar,
  PENDING_REASON,
  PendTag,
  StateGate,
  type StateGateMode,
  fmtUsdMinor,
} from './metrics-shared';

/** 无源维度（服务端 dimensions.hour/book 恒 pending，非空过滤即 422）→ 禁用按钮 + 原因 tooltip */
const DIMS_DISABLED = [
  {
    key: 'hour',
    label: '时段 (UTC)',
    reason: 'hour_bucket 无数据源：归因为日粒度 · 行为埋点未接入 · 接入后开放',
  },
  {
    key: 'book',
    label: '书籍',
    reason: 'book_id 无数据源：归因落地页绑定 + chapter_view 埋点均未就绪 · 就绪后开放',
  },
] as const;

/** 收入派生列（照原型 PEND_COLS；表头 PendTag 按服务端顶层 pending[] 显隐，单元格逐格闸控） */
const REVENUE_COLS: Array<{
  l: string;
  /** 服务端顶层 pending[] 对应 key */
  pendKey: string;
  cell: (r: DiagnosisRowView) => DerivedMetricCell;
  f: (v: number) => string;
}> = [
  { l: 'Rev_D7', pendKey: 'revenue_d7', cell: (r) => r.revenueD7Minor, f: (v) => fmtUsdMinor(v) },
  { l: 'LTV_D30', pendKey: 'ltv_d30', cell: (r) => r.ltvD30Minor, f: (v) => fmtUsdMinor(v) },
];

/** media_source 展示名：'' = 自然量（与 user_attributions 维度键对齐） */
function channelLabel(ch: string): string {
  return ch === '' ? '自然量' : ch;
}

/** CSV 字段转义：含逗号/引号/换行时加引号并转义内部引号（channel 值含逗号时防串列） */
function csvField(v: string): string {
  // CSV 公式注入防护：以 = + - @ 或 制表/回车 开头的维度名会被 Excel/Sheets 当公式求值
  // → 前缀单引号中和为纯文本（OWASP CSV Injection）。
  const safe = /^[=+\-@\t\r]/.test(v) ? `'${v}` : v;
  return /[",\n]/.test(safe) ? `"${safe.replaceAll('"', '""')}"` : safe;
}

/** 派生收入单元 → CSV 值：PENDING 保留灰显语义（导出标 PENDING、禁编造数字）；空值 → 空串 */
function derivedCsv(cell: DerivedMetricCell, fmt: (v: number) => string): string {
  if (cell.pending) return 'PENDING';
  return cell.value == null ? '' : fmt(cell.value);
}

/**
 * 明细客户端 CSV 导出（真实当前 data.rows，就地生成、无 diagnosis 明细导出端点；不接原型
 * dash-export 弹窗——那是 dashboard 周报 KPI 导出非本页明细行）。金额导出 USD 主单位（minor/100），
 * 未就绪收入列标 PENDING（对齐页面灰显、禁导出假数字）。
 */
function downloadDiagnosisCsv(rows: DiagnosisRowView[]): void {
  const usd = (minor: number) => (minor / 100).toFixed(2);
  const header = ['渠道', 'Installs', 'Rev_D7_USD', 'LTV_D30_USD'];
  const lines = [
    header.join(','),
    ...rows.map((r) =>
      [
        csvField(channelLabel(r.channel)),
        String(r.installs),
        derivedCsv(r.revenueD7Minor, usd),
        derivedCsv(r.ltvD30Minor, usd),
      ].join(','),
    ),
  ];
  const blob = new Blob([`${lines.join('\n')}\n`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `metrics-diagnosis-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function MetricsDiagnosisPage() {
  const appId = useAppScope((s) => s.appId);
  const [range, setRange] = useState<MetricsRange>('30d');
  const [channelFilter, setChannelFilter] = useState<string[]>([]);

  const { data, isLoading, isError, hasData, refetch } = useMetricsDiagnosis({
    range,
    appScope: appId,
    channel: channelFilter,
  });
  const { data: apps } = useApps();
  const currentAppName = appId === 'all' ? undefined : apps.find((a) => a.id === appId)?.name;

  const activeCount = channelFilter.length;

  // 渠道全集缓存：服务端按已选渠道过滤后，结果 rows 只剩已选渠道——若选项只从当前 rows 取，
  // 选中 Meta 后 AppLovin 会从多选面板消失、加不了第二个渠道。故在**完全无维度过滤**的响应里
  // 记住该 scope+range 的渠道全集，过滤期间选项仍展示全集
  const scopeKey = `${appId}:${range}`;
  const [channelUniverse, setChannelUniverse] = useState<{ key: string; values: string[] }>({
    key: '',
    values: [],
  });
  useEffect(() => {
    if (channelFilter.length === 0 && hasData) {
      const values = [...new Set(data.rows.map((r) => r.channel).filter((c) => c !== ''))].sort();
      setChannelUniverse({ key: scopeKey, values });
    }
  }, [channelFilter.length, hasData, data.rows, scopeKey]);

  // 渠道选项 = 全集缓存（同 scope+range）∪ 当前结果 distinct channel ∪ 已选值（可回退取消勾选）；
  // 自然量（''）不入选项——route CSV 解析去空串，无法作为过滤值下发
  const channelOptions = useMemo(() => {
    const set = new Set<string>(channelFilter);
    if (channelUniverse.key === scopeKey) for (const c of channelUniverse.values) set.add(c);
    for (const r of data.rows) {
      if (r.channel !== '') set.add(r.channel);
    }
    return [...set].sort();
  }, [data.rows, channelFilter, channelUniverse, scopeKey]);

  const clearFilters = () => {
    setChannelFilter([]);
  };

  // 四态派生（真实 query 状态，非原型演示 mode）：初次加载 → loading；失败 → error；
  // 成功、无过滤且无任何组合行 → empty（时间窗内无归因数据）；否则 normal
  // （过滤后无命中 → normal 内嵌 EmptyBlock + 清除筛选，与原型一致）
  const isEmpty = hasData && !isError && data.rows.length === 0 && activeCount === 0;
  const mode: StateGateMode = isLoading
    ? 'loading'
    : isError
      ? 'error'
      : isEmpty
        ? 'empty'
        : 'normal';

  // 「数据更新至」= 数据覆盖窗上界 window.to；computed_at 是跑批时刻（凌晨跑批会虚报一天），仅作回退
  const freshness = data.window.to
    ? `数据更新至 ${data.window.to} · T+1`
    : data.computedAt
      ? `数据更新至 ${data.computedAt.slice(0, 10)} · T+1`
      : undefined;

  return (
    <div className="page" data-screen-label="诊断下钻 L3">
      <div className="page-header">
        <div>
          <h1 className="page-title">
            诊断下钻{' '}
            <span style={{ color: 'var(--fg-faint)', fontWeight: 400, fontSize: 15 }}>L3</span>
          </h1>
          <div className="page-sub hstack" style={{ gap: 8, flexWrap: 'wrap' }}>
            任意维度组合查询 · 当前可用：渠道（归因）
            <Cal t="T+1" />
            <LiveTag
              label="live·归因"
              title="真实数据源：user_attributions（归因 cohort）· installs live"
            />
          </div>
        </div>
        {/* 导出明细：客户端 CSV 导出当前结果行（无行时禁用；不接原型 dash-export 弹窗——那是 dashboard 周报导出） */}
        <button
          type="button"
          className="btn btn-primary"
          disabled={isLoading || isError || data.rows.length === 0}
          onClick={() => {
            if (isLoading || isError) return;
            downloadDiagnosisCsv(data.rows);
            toast('明细已导出 CSV', { tone: 'pos', icon: 'IconDownload' });
          }}
        >
          <Icons.IconDownload size={13} />
          导出明细
        </button>
      </div>

      <MetricsToolbar
        appName={currentAppName}
        range={range}
        setRange={setRange}
        freshness={freshness}
      />

      {/* 维度组合查询器：live 维度可选（服务端过滤）· 无源维度禁用 + 原因 */}
      <div
        className="card"
        style={{
          padding: '10px 14px',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
        }}
      >
        <span
          className="hstack"
          style={{ gap: 6, fontSize: 12, color: 'var(--fg-muted)', flexShrink: 0 }}
        >
          <Icons.IconFilter size={13} />
          维度
        </span>
        <FilterButton
          label="渠道"
          options={channelOptions}
          value={channelFilter}
          onChange={setChannelFilter}
        />
        {DIMS_DISABLED.map((d) => (
          <span key={d.key} title={d.reason} style={{ display: 'inline-flex' }}>
            <button
              type="button"
              className="btn"
              disabled
              style={{ opacity: 0.55, cursor: 'not-allowed', gap: 6 }}
            >
              {d.label}
              <PendTag label="无数据源" k="beh" style={{ borderStyle: 'dashed' }} />
            </button>
          </span>
        ))}
        {activeCount > 0 && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={clearFilters}>
            <Icons.IconX size={11} />
            清除 {activeCount}
          </button>
        )}
        <div style={{ flex: 1 }} />
        <span className="mono" style={{ fontSize: 11, color: 'var(--fg-faint)' }}>
          {data.rows.length} 组合
        </span>
      </div>

      <IncomeCoverageNotice coverage={isError ? undefined : data.financialCoverage} />
      <StateGate
        mode={mode}
        skeleton={<TableSkeleton rows={10} cols={4} />}
        emptyTitle="没有命中该维度组合的数据"
        emptyNote="当前时间窗内无归因数据 · 请换个时间窗"
        errorNote="GET /api/v1/metrics/diagnosis 加载失败 · 请重试"
        onRetry={refetch}
      >
        {data.rows.length === 0 ? (
          <EmptyBlock
            title="没有命中该维度组合的数据"
            note="尝试放宽筛选条件，或确认该组合在所选时间窗内有归因记录"
            action={
              <button type="button" className="btn btn-sm" onClick={clearFilters}>
                清除全部筛选
              </button>
            }
          />
        ) : (
          <div className="card" style={{ overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <Table className="tbl" style={{ tableLayout: 'auto' }}>
                <thead>
                  <tr>
                    <th>渠道</th>
                    <th className="num" title="user_attributions 归因口径 · live">
                      Installs
                    </th>
                    {REVENUE_COLS.map((c) => {
                      const colPending = data.pending.includes(c.pendKey);
                      return (
                        <th
                          key={c.l}
                          className="num"
                          title={
                            colPending ? PENDING_REASON.rev : 'USD·运营口径 · 收入派生（就绪）'
                          }
                        >
                          <span className="hstack" style={{ gap: 5, justifyContent: 'flex-end' }}>
                            {c.l} {colPending && <PendTag k="rev" label="P·收入" />}
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={r.channel}>
                      <td style={{ fontWeight: 500, fontSize: 12.5, whiteSpace: 'nowrap' }}>
                        {channelLabel(r.channel)}
                      </td>
                      <td className="num mono">{r.installs.toLocaleString()}</td>
                      {REVENUE_COLS.map((c) => {
                        const cell = c.cell(r);
                        return cell.pending ? (
                          <td
                            key={c.l}
                            className="num mono"
                            title={PENDING_REASON.rev}
                            style={{ background: 'var(--bg-subtle)', color: 'var(--fg-faint)' }}
                          >
                            —
                          </td>
                        ) : (
                          <td key={c.l} className="num mono">
                            {cell.value == null ? '—' : c.f(cell.value)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            <div
              style={{
                padding: '8px 14px',
                borderTop: '1px solid var(--border)',
                fontSize: 10.5,
                color: 'var(--fg-faint)',
                background: 'var(--bg-subtle)',
                display: 'flex',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 8,
              }}
            >
              <span>
                收入列（Rev_D7 / LTV_D30）待收入接入后自动点亮 · 时段/书籍维度接入后自动开放
              </span>
              <span className="mono">SQL 下推 Neon · 预聚合只读</span>
            </div>
          </div>
        )}
      </StateGate>
    </div>
  );
}
